/**
 * Storage durability: does this browser intend to keep what we wrote?
 *
 * Safari throws script-writable storage away two ways — the seven-day cleanup
 * for sites only ever used in a tab, and eviction under disk pressure — and
 * roughly nine in ten players are on iOS. The asymmetry that shapes all of this
 * is that game data is enormous but replaceable (the player still owns their
 * copy; re-importing is one drag) while saves are tiny and irreplaceable. So
 * nothing here tries to make eviction impossible. It tries to make eviction
 * cost a re-import rather than a playthrough.
 *
 * See docs/design/save-backup-durability.md.
 */

import { activeIdbfsDbName, activeSaveDir } from './config';
import { getMeta, setMeta, requestPersistence, storageEstimate, type InstallMeta } from './store';

/**
 * Facts about this origin that must outlive the vault.
 *
 * localStorage and IndexedDB are usually evicted together, which is exactly why
 * a same-origin canary can only ever be best-effort (below). But they are
 * *cleared* separately often enough — a version bump, a failed upgrade, one
 * store deleted and not the other — that keeping the durability record outside
 * the vault it describes is still worth doing.
 */
const KEY_PERSIST = 'rr.durability.persist';
const KEY_CANARY = 'rr.durability.canary';
const KEY_LAST_BACKUP = 'rr.durability.lastBackup';

/** Safari in private mode throws on write; a durability record is never worth an exception. */
function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* no local storage to speak of; every caller degrades to "unknown" */
  }
}

function readLocalJson<T>(key: string): T | null {
  const raw = readLocal(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

// --- 1. persistent storage ---------------------------------------------------

export interface PersistRecord {
  granted: boolean;
  /** When we last asked. Purely for the health panel; we never ask twice over. */
  askedAt: number;
}

export function persistRecord(): PersistRecord | null {
  return readLocalJson<PersistRecord>(KEY_PERSIST);
}

/**
 * Ask for persistent storage once, at the moment of clearest intent: the player
 * has just handed us half a gigabyte of their own game.
 *
 * Never blocks anything and never nags. A refusal is recorded and surfaced in
 * the storage-health line; it does not produce a dialog, a banner, or a retry.
 */
export async function recordPersistGrant(): Promise<PersistRecord> {
  const granted = await requestPersistence();
  const record: PersistRecord = { granted, askedAt: Date.now() };
  writeLocal(KEY_PERSIST, JSON.stringify(record));
  return record;
}

// --- 2. eviction canary ------------------------------------------------------

interface Canary {
  /** Last time we observed a populated vault. */
  vaultAt: number;
  /** Slot directories present at that moment; 0 means "vault but no saves yet". */
  slots: number;
  savesAt: number | null;
}

/** What the canary can tell us at boot. `unknown` is the honest common case. */
export interface EvictionVerdict {
  state: 'ok' | 'unknown' | 'evicted';
  /** What the canary says used to be here and no longer is. */
  lostGameData: boolean;
  lostSaves: boolean;
  /** Slot count the canary last recorded, for the recovery copy. */
  knownSlots: number;
}

const FRESH: EvictionVerdict = { state: 'unknown', lostGameData: false, lostSaves: false, knownSlots: 0 };

let verdict: EvictionVerdict = FRESH;

/** The last verdict `assessStorage()` reached. Read by the recovery UI. */
export const evictionVerdict = (): EvictionVerdict => verdict;

/**
 * Counts persisted slot directories without booting the engine.
 *
 * Keys only: IDBFS stores each file's bytes in the record, and at boot we want
 * the shape of the tree, not its contents.
 *
 * Returns null for "could not read", which is emphatically not the same answer
 * as zero. A second tab holding the database open, a blocked upgrade, an
 * aborted transaction — all of them are transient, and all of them used to
 * arrive here as 0 and come out of the verdict below as "your browser cleared
 * this site's storage". A banner that cries wolf about data loss is worse than
 * no banner at all, so the two cases are kept apart at the source.
 */
export async function countPersistedSlots(): Promise<number | null> {
  let db: IDBDatabase;
  try {
    db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(activeIdbfsDbName());
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('save archive unavailable'));
      request.onblocked = () => reject(new Error('save archive is busy'));
    });
  } catch {
    return null;
  }
  try {
    // A database with no FILE_DATA store is one we just created by opening it:
    // a real, readable, empty save archive rather than a failure.
    if (!db.objectStoreNames.contains('FILE_DATA')) return 0;
    const saveRoot = activeSaveDir();
    return await new Promise<number>((resolve, reject) => {
      const slots = new Set<string>();
      const transaction = db.transaction('FILE_DATA', 'readonly');
      const cursorRequest = transaction.objectStore('FILE_DATA').openKeyCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) return;
        const relative = String(cursor.key).startsWith(`${saveRoot}/`)
          ? String(cursor.key).slice(saveRoot.length + 1)
          : '';
        const match = /^(SLOT\d+)\//i.exec(relative);
        if (match) slots.add(match[1].toUpperCase());
        cursor.continue();
      };
      transaction.oncomplete = () => resolve(slots.size);
      transaction.onerror = () => reject(transaction.error ?? new Error('save archive unreadable'));
      transaction.onabort = () => reject(transaction.error ?? new Error('save archive unreadable'));
    });
  } catch {
    return null;
  } finally {
    db.close();
  }
}

/**
 * Compares what used to be here against what is here now.
 *
 * The honest limit, stated in the design doc: a same-origin canary cannot
 * reliably tell "Safari wiped us" from "first visit", because eviction takes
 * localStorage and IndexedDB together. What it *can* catch is the partial case
 * — a surviving canary next to an empty vault — and that case is worth catching
 * because it is the one where we can name what happened instead of showing a
 * returning player the first-run screen. (The stronger signal, an HTTP-set
 * functional cookie that survives script-storage cleanup, is deliberately not
 * built: it needs a wording check against the landing page's no-tracking
 * promise first. Decision owner: Ben.)
 *
 * Also refreshes the canary, so this is the one call the boot path needs.
 */
export async function assessStorage(): Promise<EvictionVerdict> {
  verdict = await computeVerdict();
  return verdict;
}

async function computeVerdict(): Promise<EvictionVerdict> {
  const previous = readLocalJson<Canary>(KEY_CANARY);

  // "Absent" and "unreadable" are different answers on both halves of the
  // vault, and only the first of them is evidence of anything. A rejected
  // getMeta is a broken or busy database, not a missing install.
  let install: InstallMeta | undefined;
  let installReadable = true;
  try {
    install = await getMeta<InstallMeta>('install');
  } catch {
    installReadable = false;
  }
  const slots = await countPersistedSlots();
  const haveSaves = (slots ?? 0) > 0;

  const canary: Canary = {
    vaultAt: install ? Date.now() : previous?.vaultAt ?? 0,
    // Never downgraded by a read we could not make: the canary records the most
    // we have ever seen here, and an unreadable database has told us nothing.
    slots: Math.max(slots ?? 0, previous?.slots ?? 0),
    savesAt: haveSaves ? Date.now() : previous?.savesAt ?? null,
  };
  if (install || haveSaves) {
    writeLocal(KEY_CANARY, JSON.stringify(canary));
    // The IDB half of the canary. It rides in the vault so that a vault which
    // survives can say when it was last seen intact; a vault which does not
    // survive takes this with it, which is the whole signal.
    void setMeta('durabilityCanary', { seenAt: Date.now(), slots: slots ?? 0 }).catch(() => {});
  }

  if (!previous) return install || haveSaves ? { ...FRESH, state: 'ok' } : FRESH;

  // Loss is only ever claimed from a reading we actually got.
  const lostGameData = installReadable && previous.vaultAt > 0 && !install;
  const lostSaves = slots !== null && previous.slots > 0 && slots === 0;
  if (!lostGameData && !lostSaves) {
    return { ...FRESH, state: 'ok', knownSlots: slots ?? previous.slots };
  }
  return { state: 'evicted', lostGameData, lostSaves, knownSlots: previous.slots };
}

/**
 * Forgets that anything was ever here.
 *
 * Must be called by anything that deliberately empties the vault. The canary
 * lives outside the store it describes, so a wipe we performed ourselves looks
 * exactly like a wipe Safari performed — and the next boot would open with
 * "your browser cleared this site's storage" over a deletion the player asked
 * for. `store.clearVault()` calls this; that is the enforcement.
 */
export function clearDurabilityCanary(): void {
  try {
    localStorage.removeItem(KEY_CANARY);
  } catch {
    /* nothing was recorded, so nothing can be wrongly remembered */
  }
  verdict = FRESH;
}

// --- 3. storage health -------------------------------------------------------

export interface StorageHealth {
  /** null when the browser has no Storage API to ask. */
  persisted: boolean | null;
  /** What we recorded at import time, which may predate a later grant. */
  askedAt: number | null;
  usage: number | null;
  quota: number | null;
  lastBackupAt: number | null;
  /** Set by the backup layer once a destination is connected. */
  destination: string | null;
}

export function lastBackupAt(): number | null {
  const raw = readLocal(KEY_LAST_BACKUP);
  const value = raw ? Number(raw) : NaN;
  return Number.isFinite(value) ? value : null;
}

export function noteBackupTaken(at = Date.now()): void {
  writeLocal(KEY_LAST_BACKUP, String(at));
}

/** Where automatic backups are being written, when anything is connected. */
let backupDestination: string | null = null;

export function setBackupDestination(name: string | null): void {
  backupDestination = name;
}

export async function storageHealth(): Promise<StorageHealth> {
  const record = persistRecord();
  let persisted: boolean | null = record?.granted ?? null;
  try {
    if (navigator.storage?.persisted) persisted = await navigator.storage.persisted();
  } catch {
    /* keep the recorded answer */
  }
  const estimate = await storageEstimate().catch(() => null);
  return {
    persisted,
    askedAt: record?.askedAt ?? null,
    usage: estimate?.usage ?? null,
    quota: estimate?.quota ?? null,
    lastBackupAt: lastBackupAt(),
    destination: backupDestination,
  };
}

// --- 4. platform ------------------------------------------------------------

/**
 * iOS Safari in a normal tab — the only place the Add to Home Screen chip makes
 * sense, because it is the only platform with no install API and the only one
 * whose storage is on a seven-day timer.
 *
 * iPadOS reports itself as a Mac, hence the touch-point check. Chrome and
 * Firefox on iOS are still WebKit and still evicted, but they cannot install to
 * the home screen at all, so they are excluded rather than told to do the
 * impossible.
 */
export function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!iOS) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

/** True inside an installed home-screen app, where none of the nudges belong. */
export function isStandalone(): boolean {
  return (
    matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}
